/**
 * GeoLocationValidator
 * 
 * Validates geolocation results using RTT (Round-Trip Time) measurements.
 * Uses the physical constraint of light speed in fiber (~200 km/ms round-trip)
 * to detect impossible or unlikely geolocation results.
 * 
 * This is particularly useful for detecting incorrect anycast IP geolocations
 * where databases return a single location (often HQ) for IPs that serve
 * from multiple geographic locations.
 */

export type ValidationResult = 'valid' | 'unlikely' | 'impossible';

export interface ValidationDetails {
    result: ValidationResult;
    distanceKm: number;
    measuredRttMs: number;
    theoreticalMinRttMs: number;
    confidence: 'high' | 'medium' | 'low';
}

/**
 * Speed of light in fiber optic cable (approximate)
 * ~200,000 km/s = 200 km/ms for one-way
 * For round-trip: ~100 km/ms (conservative estimate accounting for routing overhead)
 */
const SPEED_KM_PER_MS_ROUNDTRIP = 100;

/**
 * Threshold multipliers for validation
 * - Below 0.3x theoretical minimum = physically impossible
 * - Below 0.5x theoretical minimum = very unlikely (possible with heavy overhead)
 */
const IMPOSSIBLE_THRESHOLD = 0.7; // Even stricter to catch low-latency mismatches
const UNLIKELY_THRESHOLD = 0.8;   // Tightened for better accuracy

export class GeoLocationValidator {
    /**
     * Validate a geolocation result by comparing measured RTT against
     * the theoretical minimum RTT based on geographic distance.
     * 
     * @param userLat - User's latitude
     * @param userLon - User's longitude
     * @param hopLat - Hop's reported latitude
     * @param hopLon - Hop's reported longitude
     * @param measuredRttMs - Measured round-trip time in milliseconds
     * @returns ValidationDetails with result and supporting data
     */
    public static validate(
        userLat: number,
        userLon: number,
        hopLat: number,
        hopLon: number,
        measuredRttMs: number
    ): ValidationDetails {
        const distanceKm = this.haversineDistance(userLat, userLon, hopLat, hopLon);
        const theoreticalMinRttMs = distanceKm / SPEED_KM_PER_MS_ROUNDTRIP;

        let result: ValidationResult;
        let confidence: 'high' | 'medium' | 'low';

        if (measuredRttMs < theoreticalMinRttMs * IMPOSSIBLE_THRESHOLD) {
            result = 'impossible';
            confidence = 'low';
        } else if (measuredRttMs < theoreticalMinRttMs * UNLIKELY_THRESHOLD) {
            result = 'unlikely';
            confidence = 'low';
        } else {
            result = 'valid';
            confidence = 'high';
        }

        return {
            result,
            distanceKm,
            measuredRttMs,
            theoreticalMinRttMs,
            confidence
        };
    }

    /**
     * Quick validation check - returns just the result without details
     * 
     * @param userLat - User's latitude
     * @param userLon - User's longitude
     * @param hopLat - Hop's reported latitude
     * @param hopLon - Hop's reported longitude
     * @param measuredRttMs - Measured round-trip time in milliseconds
     * @returns ValidationResult: 'valid', 'unlikely', or 'impossible'
     */
    public static validateQuick(
        userLat: number,
        userLon: number,
        hopLat: number,
        hopLon: number,
        measuredRttMs: number
    ): ValidationResult {
        return this.validate(userLat, userLon, hopLat, hopLon, measuredRttMs).result;
    }

    /**
     * Check if a location is plausible for a given RTT
     * Returns true if location could be valid, false if impossible/unlikely
     */
    public static isPlausible(
        userLat: number,
        userLon: number,
        hopLat: number,
        hopLon: number,
        measuredRttMs: number
    ): boolean {
        return this.validateQuick(userLat, userLon, hopLat, hopLon, measuredRttMs) === 'valid';
    }

    /**
     * Calculate maximum plausible distance for a given RTT
     * Useful for determining search radius when location is unknown
     * 
     * @param measuredRttMs - Measured RTT in milliseconds
     * @returns Maximum plausible distance in kilometers
     */
    public static maxPlausibleDistance(measuredRttMs: number): number {
        return measuredRttMs * SPEED_KM_PER_MS_ROUNDTRIP;
    }

    /**
     * Calculate the distance between two coordinates using Haversine formula
     * 
     * @param lat1 - First point latitude
     * @param lon1 - First point longitude
     * @param lat2 - Second point latitude
     * @param lon2 - Second point longitude
     * @returns Distance in kilometers
     */
    public static haversineDistance(
        lat1: number,
        lon1: number,
        lat2: number,
        lon2: number
    ): number {
        const R = 6371; // Earth's radius in km
        const dLat = this.toRadians(lat2 - lat1);
        const dLon = this.toRadians(lon2 - lon1);

        const a =
            Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(this.toRadians(lat1)) *
            Math.cos(this.toRadians(lat2)) *
            Math.sin(dLon / 2) *
            Math.sin(dLon / 2);

        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

        return R * c;
    }

    private static toRadians(degrees: number): number {
        return degrees * (Math.PI / 180);
    }
}
